import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/useSidebar';
import { useAssistant } from '../contexts/AssistantContext';
import { usePermission } from '../hooks/usePermission';
import { useAfterFirstPaint } from '../hooks/useAfterFirstPaint';
import { useUserOrganizations, useWhatsNewQuery } from '../hooks/queries/useAccountShellQueries';
import { markInitialPaintComplete } from '../utils/afterInitialPaint';
import { GlobalSearchBar } from '../components/GlobalSearchBar';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { SlaBreachIndicator } from '../components/notifications/SlaBreachIndicator';
import { UnassignedIndicator } from '../components/notifications/UnassignedIndicator';
import { NotificationPanel } from '../components/notifications/NotificationPanel';
import { AssistantPane } from '../components/assistant/AssistantPane';
import { BookmarkViewModal } from '../components/navigation/BookmarkViewModal';
import { FavouritesMenu } from '../components/navigation/FavouritesMenu';
import { QuickLinksMenu } from '../components/navigation/QuickLinksMenu';
import { AllLinksMenu } from '../components/navigation/AllLinksMenu';
import { FeedbackButton } from '../components/FeedbackButton';
import { UserAvatar } from '../components/UserAvatar';
import { SwitchOrganizationModal } from '../components/SwitchOrganizationModal';
import { WhatsNewModal } from '../components/common/WhatsNewModal';
import { apiClient } from '../services/api';

interface MainLayoutProps {
  children: ReactNode;
  constrainContent?: boolean; // If true, content is constrained with max-width and padding
}

interface MenuItem {
  name: string;
  path?: string;
  icon: string;
  description: string;
  subItems?: MenuItem[];
}

export function MainLayout({ children, constrainContent = true }: MainLayoutProps) {
  const appVersion = import.meta.env.VITE_APP_VERSION || '0.112.1';
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const incidentRouteId = useMatch('/incidents/:id')?.params.id;
  const waitsForIncidentPaint = Boolean(incidentRouteId && incidentRouteId !== 'all' && incidentRouteId !== 'new');
  const hasInitialShellPaint = useAfterFirstPaint();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(min-width: 768px)').matches;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = useState(false);
  const [showSwitchOrganizationModal, setShowSwitchOrganizationModal] = useState(false);
  const [failedBrandLogoUrl, setFailedBrandLogoUrl] = useState<string | null>(null);
  const { toggleMenu, isMenuExpanded } = useSidebar();
  const [hoveredCollapsedMenu, setHoveredCollapsedMenu] = useState<string | null>(
    null,
  );
  const collapsedFlyoutHideTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { togglePane, isOpen: isAssistantOpen, messages: assistantMessages } = useAssistant();
  const sidebarWidth = isDesktop ? (isSidebarOpen ? 256 : 64) : 0;
  const mainStyle = {
    '--sidebar-width': `${sidebarWidth}px`,
  } as CSSProperties;

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia('(min-width: 768px)');

    const handleDesktopChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
      if (!event.matches) {
        setHoveredCollapsedMenu(null);
      }
    };

    desktopMediaQuery.addEventListener('change', handleDesktopChange);

    return () => {
      desktopMediaQuery.removeEventListener('change', handleDesktopChange);
    };
  }, []);

  useEffect(() => {
    if (waitsForIncidentPaint || !hasInitialShellPaint) {
      return;
    }

    const frame = window.requestAnimationFrame(markInitialPaintComplete);
    return () => window.cancelAnimationFrame(frame);
  }, [hasInitialShellPaint, location.pathname, waitsForIncidentPaint]);

  useEffect(() => {
    return () => {
      if (collapsedFlyoutHideTimer.current) {
        clearTimeout(collapsedFlyoutHideTimer.current);
      }
    };
  }, []);

  const { data: organizations } = useUserOrganizations();
  const currentOrganization =
    organizations?.find((organization) => organization.isCurrent) ??
    organizations?.find((organization) => organization.tenantId === user?.tenantId);
  const tenantDisplayName = currentOrganization?.tenantName?.trim() || null;
  const { data: whatsNewData, dismiss: dismissWhatsNew } = useWhatsNewQuery();

  const isSystemAdmin = user?.role === 'SystemAdmin';
  const isTenantAdmin = user?.role === 'TenantAdmin' || isSystemAdmin;
  const canViewDiscoveryJobs = usePermission('cmdb_discovery.view');
  const canViewIntegrations = usePermission('integrations.view');
  const canViewTeamsIntegration = usePermission('teams_integration.view');
  const canViewSettings = usePermission('settings.read');
  const { data: integrations } = useQuery({
    queryKey: ['integrations', 'layout', user?.tenantId],
    queryFn: () => apiClient.getIntegrations(),
    enabled: Boolean(user?.tenantId && canViewIntegrations && canViewTeamsIntegration),
    staleTime: 30_000,
  });
  const isTeamsConfiguredAndEnabled = Boolean(
    integrations?.some((integration: { type: string; isEnabled: boolean }) => integration.type === 'teams' && integration.isEnabled),
  );

  const navigation: MenuItem[] = [
    {
      name: 'Dashboard',
      icon: '📊',
      description: 'Overview and metrics',
      subItems: [
        {
          name: 'Events Dashboard',
          path: '/events-dashboard',
          icon: '📡',
          description: 'Monitor event processing activity',
        },
        {
          name: 'Asset Overview',
          path: '/dashboards/assets',
          icon: '🗄️',
          description: 'CMDB overview and metrics',
        },
        {
          name: 'Overview',
          path: '/dashboard',
          icon: '📊',
          description: 'Overview and metrics',
        },
        {
          name: 'Reports',
          path: '/reports',
          icon: '📈',
          description: 'Incident reports and SLA analytics',
        },
        {
          name: 'Operational Insights',
          path: '/operational-insights',
          icon: '📉',
          description: 'Operational trends and analytics',
        },
      ],
    },
    {
      name: 'Incidents',
      icon: '🎫',
      description: 'Incident management',
      subItems: [
        {
          name: 'All Incidents',
          path: '/incidents/all',
          icon: '📊',
          description: 'Browse all incidents with advanced filtering',
        },
        {
          name: 'My Queue',
          path: '/incidents',
          icon: '📋',
          description: 'Team-based incident queue',
        },
      ],
    },
    {
      name: 'On-Call',
      icon: '📞',
      description: 'On-call schedules, escalation, and holidays',
      subItems: [
        {
          name: 'Escalation Policies',
          path: '/admin/escalation-policies',
          icon: '🪜',
          description: 'Configure escalation flows',
        },
        {
          name: 'Holiday Calendars',
          path: '/admin/holiday-calendars',
          icon: '🎉',
          description: 'Manage holiday calendars',
        },
        {
          name: 'Schedules',
          path: '/admin/on-call',
          icon: '🗓️',
          description: 'Manage on-call schedules',
        },
      ],
    },
    {
      name: 'Event Management',
      icon: '🔌',
      description: 'Ingestion endpoints, event history, and pipeline metrics',
      subItems: [
        {
          name: 'Episodes',
          path: '/episodes',
          icon: '🔗',
          description: 'Correlated alert episodes',
        },
        {
          name: 'Event History',
          path: '/events',
          icon: '📡',
          description: 'View incoming events',
        },
        {
          name: 'Ingestion Endpoints',
          path: '/ingestion-endpoints',
          icon: '🔌',
          description: 'Manage ingestion endpoints',
        },
        {
          name: 'Correlation Rules',
          path: '/correlation-rules',
          icon: '🔗',
          description: 'Manage event correlation',
        },
      ],
    },
    ...(isTenantAdmin
      ? [{
          name: 'Automation',
          icon: '⚙️',
          description: 'Suppression rules and automation scripts',
          subItems: [
            {
              name: 'Suppression Rules',
              path: '/suppression-rules',
              icon: '🛡️',
              description: 'Manage suppression logic',
            },
            {
              name: 'Script Library',
              path: '/scripts',
              icon: '📜',
              description: 'Manage automation scripts for enrichment lookups',
            },
          ],
        }]
      : []),
    {
      name: 'CMDB',
      icon: '🗄️',
      description: 'Configuration Management Database',
      subItems: [
        {
          name: 'Azure Hierarchy',
          path: '/cmdb/azure-hierarchy',
          icon: '🌳',
          description: 'Browse discovered Azure hierarchy',
        },
        {
          name: 'CI Catalog',
          path: '/cmdb/catalog',
          icon: '📋',
          description: 'Browse system CI catalog',
        },
        {
          name: 'CI Classes',
          path: '/cmdb/classes',
          icon: '🗂️',
          description: 'Manage configuration classes',
        },
        {
          name: 'CI Items',
          path: '/cmdb/items',
          icon: '🧩',
          description: 'Manage configuration items',
        },
        ...(canViewDiscoveryJobs
          ? [
              {
                name: 'Discovery Jobs',
                path: '/admin/integrations/discovery-jobs',
                icon: '📜',
                description: 'View discovery job history',
              },
            ]
          : []),
        {
          name: 'Predefined Tags',
          path: '/cmdb/predefined-tags',
          icon: '🏷️',
          description: 'Manage reusable predefined tag keys',
        },
      ],
    },
    {
      name: 'Tables',
      path: '/tables',
      icon: '🗂️',
      description: 'Browse all available tables',
    },
    {
      name: 'Notification Center',
      path: '/notifications',
      icon: '🔔',
      description: 'View all notifications',
    },
  ];

  const documentationNavigation: MenuItem[] = [
    {
      name: 'Documentation',
      path: '/docs/getting-started',
      icon: '📚',
      description: 'User guides and setup docs',
    },
  ];

  // Add admin navigation for SystemAdmin and TenantAdmin users
  const adminNavigation: MenuItem[] = isTenantAdmin
    ? [
        {
          name: 'User Management',
          icon: '👤',
          description: 'Manage users, roles, and assignments',
          subItems: [
            {
              name: 'Role Assignments',
              path: '/admin/role-assignments',
              icon: '🎯',
              description: 'Assign roles with scopes',
            },
            {
              name: 'Roles & Permissions',
              path: '/admin/roles',
              icon: '🔐',
              description: 'Manage roles and permissions',
            },
            {
              name: 'Users',
              path: '/admin/users',
              icon: '👤',
              description: 'Manage users',
            },
          ],
        },
        {
          name: 'Integrations',
          icon: '🔗',
          description: 'Manage integrations and connections',
          subItems: [
            {
              name: 'Add Integration',
              path: '/admin/integrations',
              icon: '➕',
              description: 'Browse and add new integrations',
            },
            {
              name: 'My Integrations',
              path: '/admin/integrations/configured',
              icon: '🧩',
              description: 'Manage configured integrations',
            },
          ],
        },
        {
          name: 'Tenant Configuration',
          icon: '🛠️',
          description: 'Teams, SLA policies, and settings',
          subItems: [
            {
              name: 'Customers',
              path: '/customers',
              icon: '👥',
              description: 'Manage customers',
            },
            {
              name: 'ITIL Configuration',
              path: '/admin/itil-configuration',
              icon: '📋',
              description: 'Priority matrix, reason codes, and SLA policies',
            },
            {
              name: 'SLA Schedules',
              path: '/admin/sla-schedules',
              icon: '⏰',
              description: 'Working hours used by SLA/OLA/UC agreements',
            },
            {
              name: 'Settings',
              path: '/settings/tenant',
              icon: '⚙️',
              description: 'Tenant configuration',
            },
            ...(canViewSettings
              ? [{
                  name: 'SLA Platform Defaults',
                  path: '/settings/sla-platform-defaults',
                  icon: '⏱️',
                  description: 'Tenant-wide runtime fallbacks for agreement SLA fields',
                }]
              : []),
            {
              name: 'AI Usage',
              path: '/settings/ai-usage',
              icon: '🤖',
              description: 'Portal vs Teams AI usage reporting',
            },
            {
              name: 'Branding',
              path: '/settings/branding',
              icon: '🎨',
              description: 'Logo and theme customization',
            },
            {
              name: 'Teams',
              path: '/admin/teams',
              icon: '👥',
              description: 'Manage teams',
            },
          ],
        },
      ]
    : [];

  const teamsNavigation: MenuItem[] =
    canViewTeamsIntegration && isTeamsConfiguredAndEnabled
      ? [
          {
            name: 'Teams Integration',
            icon: '💬',
            description: 'Teams dashboard, analytics, and AI usage',
            subItems: [
              {
                name: 'Dashboard',
                path: '/settings/teams?tab=connection',
                icon: '📊',
                description: 'Connection status and user mappings',
              },
              {
                name: 'Analytics',
                path: '/settings/teams?tab=analytics',
                icon: '📈',
                description: 'Teams engagement and usage analytics',
              },
              {
                name: 'AI Usage',
                path: '/settings/ai-usage?source=Teams',
                icon: '🤖',
                description: 'Teams AI usage metrics and costs',
              },
            ],
          },
        ]
      : [];

  const allNavigation = [
    ...navigation,
    ...teamsNavigation,
    ...adminNavigation,
    ...documentationNavigation,
  ];

  const isActive = (path?: string) => {
    if (!path) return false;
    const [targetPathname, targetQuery = ''] = path.split('?');
    const currentQueryParams = new URLSearchParams(location.search);
    const targetQueryParams = new URLSearchParams(targetQuery);
    const queryMatches = [...targetQueryParams.entries()].every(([key, value]) => {
      if (
        targetPathname === '/settings/teams' &&
        key === 'tab' &&
        value === 'connection' &&
        !currentQueryParams.has('tab')
      ) {
        return true;
      }

      return currentQueryParams.get(key) === value;
    });

    // Exact match for known leaf routes to avoid prefix collisions
    // e.g., /incidents should not match /incidents/all
    if (location.pathname === targetPathname) {
      return queryMatches;
    }

    if (targetQueryParams.size > 0) {
      return false;
    }

    // For prefix matching, require a path separator after the match
    // so /incidents doesn't match /incidents/all but /incidents/123 does match /incidents
    if (location.pathname.startsWith(targetPathname + '/')) {
      // Don't prefix-match if a more specific sibling route exists
      // e.g., /incidents/all is its own route, not a child of /incidents
      const knownSubRoutes = [
        '/incidents/all',
        '/admin/integrations/configured',
        '/admin/integrations/discovery-jobs',
        '/settings/teams',
        '/settings/ai-usage',
        '/admin/integrations/teams-analytics',
      ];
      if (knownSubRoutes.some((sub) => location.pathname.startsWith(sub) && targetPathname !== sub)) {
        return false;
      }
      return true;
    }
    return false;
  };

  const hasActiveSubItem = (item: MenuItem) => {
    return item.subItems?.some((subItem) => isActive(subItem.path)) ?? false;
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((previouslyOpen) => {
      const willBeOpen = !previouslyOpen;
      if (willBeOpen) {
        setHoveredCollapsedMenu(null);
      }
      return willBeOpen;
    });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const showCollapsedFlyout = (menuName: string) => {
    if (collapsedFlyoutHideTimer.current) {
      clearTimeout(collapsedFlyoutHideTimer.current);
    }
    setHoveredCollapsedMenu(menuName);
  };

  const hideCollapsedFlyoutWithDelay = () => {
    if (collapsedFlyoutHideTimer.current) {
      clearTimeout(collapsedFlyoutHideTimer.current);
    }
    collapsedFlyoutHideTimer.current = setTimeout(() => {
      setHoveredCollapsedMenu(null);
    }, 150);
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Top Navigation Bar */}
      <nav
        className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 fixed w-full top-0 z-30"
        style={{ backgroundColor: 'var(--color-topbar-bg)' }}
        role="navigation"
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                onClick={toggleMobileMenu}
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>

              {/* Desktop sidebar toggle */}
              <button
                type="button"
                className="hidden md:inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 mr-3"
                onClick={toggleSidebar}
                aria-expanded={isSidebarOpen}
                aria-label="Toggle sidebar"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              <div className="flex-shrink-0">
                <div className="flex items-start">
                  {(() => {
                    const hasValidLogo = branding.logoUrl && failedBrandLogoUrl !== branding.logoUrl;
                    
                    return (
                      <>
                        {hasValidLogo ? (
                          <>
                            <div className="flex flex-col">
                              <img
                                src={branding.logoUrl}
                                alt="Tenant logo"
                                className="mr-2 mt-0.5 max-h-8 w-auto max-w-[180px] object-contain"
                                onError={() => setFailedBrandLogoUrl(branding.logoUrl ?? null)}
                              />
                              {tenantDisplayName ? (
                                <p className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {tenantDisplayName}
                                </p>
                              ) : null}
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col">
                            <h1 className="text-xl font-bold leading-tight text-primary-600 dark:text-primary-400">
                              CloudIncidents
                            </h1>
                            {tenantDisplayName ? (
                              <p className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-400 dark:text-gray-500 -mt-0.5">
                                {tenantDisplayName}
                              </p>
                            ) : null}
                          </div>
                        )}
                        <span className="ml-2 mt-1 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 rounded">
                          MSP
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Center - Global Search Bar + Nav Links */}
            <div className="hidden lg:flex flex-1 max-w-4xl px-4 items-center gap-2">
              <GlobalSearchBar />
              <AllLinksMenu />
              <QuickLinksMenu />
              <FavouritesMenu />
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={togglePane}
                className="relative inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-violet-600 to-purple-500 px-3 py-1.5 text-sm font-medium text-white shadow-md transition-all duration-200 hover:from-violet-500 hover:to-purple-400 hover:shadow-lg hover:shadow-purple-500/25"
                aria-label="Toggle Beacon assistant"
                title="Beacon assistant"
              >
                <span aria-hidden="true">✨</span>
                Beacon
                {!isAssistantOpen && assistantMessages.length > 0 && (
                  <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-fuchsia-200 ring-2 ring-violet-700" />
                )}
              </button>
              <button
                onClick={() => {
                  const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
                  setTheme(next);
                }}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
                title={`Theme: ${theme} (click to change)`}
              >
                {resolvedTheme === 'dark' ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </button>

              <UnassignedIndicator />
              <SlaBreachIndicator />
              <NotificationBell />
              <button
                type="button"
                onClick={() => setIsBookmarkModalOpen(true)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Bookmark this page"
                title="Bookmark this page"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21l-7.5-4.5L4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                  />
                </svg>
              </button>

              <button
                onClick={() => navigate('/incidents/new')}
                className="hidden sm:inline-flex btn-primary"
                aria-label="Create new incident"
              >
                + New Incident
              </button>

              {/* User menu dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-2 focus:outline-primary-500"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user?.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user?.role || 'User'}
                    </p>
                  </div>
                  <UserAvatar
                    tenantId={user?.tenantId ?? ''}
                    userId={user?.userId ?? ''}
                    userName={user?.email ?? 'U'}
                    size="sm"
                  />
                  <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {isUserMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsUserMenuOpen(false)}
                      aria-hidden="true"
                    />
                    
                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-56 origin-top-right bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-20">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate('/profile');
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          role="menuitem"
                        >
                          <svg className="h-5 w-5 mr-3 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          My Profile
                        </button>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            setShowSwitchOrganizationModal(true);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          role="menuitem"
                        >
                          <svg className="h-5 w-5 mr-3 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          Switch Organization
                        </button>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate('/auth/signin-logs');
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          role="menuitem"
                        >
                          <svg className="h-5 w-5 mr-3 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Sign-In Logs
                        </button>
                        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Theme</p>
                          <div className="flex gap-1">
                            {(['system', 'light', 'dark'] as const).map((t) => (
                              <button
                                key={t}
                                onClick={() => setTheme(t)}
                                className={`flex-1 px-2 py-1 text-xs rounded capitalize ${
                                  theme === t
                                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                }`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="border-t border-gray-100 dark:border-gray-700"></div>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            logout();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          role="menuitem"
                        >
                          <svg className="h-5 w-5 mr-3 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            role="menu"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {allNavigation.map((item) => (
                <div key={item.name}>
                  {item.path ? (
                    <Link
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-3 py-2 rounded-md text-base font-medium ${
                        isActive(item.path)
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      role="menuitem"
                    >
                      <span className="mr-2" aria-hidden="true">
                        {item.icon}
                      </span>
                      {item.name}
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleMenu(item.name)}
                        className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center justify-between"
                        aria-expanded={isMenuExpanded(item.name)}
                        role="menuitem"
                      >
                        <span>
                          <span className="mr-2" aria-hidden="true">
                            {item.icon}
                          </span>
                          {item.name}
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isMenuExpanded(item.name) ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {isMenuExpanded(item.name) && item.subItems && (
                        <div className="ml-4 space-y-1">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path!}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-3 py-2 rounded-md text-sm ${
                                isActive(subItem.path)
                                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700'
                                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                              aria-current={
                                isActive(subItem.path) ? 'page' : undefined
                              }
                              role="menuitem"
                            >
                              <span className="mr-2" aria-hidden="true">
                                {subItem.icon}
                              </span>
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/incidents/new');
                }}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50"
                role="menuitem"
              >
                + New Incident
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden md:block fixed left-0 top-16 bottom-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out z-20 ${
          isSidebarOpen ? 'w-64' : 'w-16'
        }`}
        style={{ backgroundColor: 'var(--color-sidebar-bg)' }}
        aria-label="Sidebar navigation"
      >
        <nav className="h-full py-4 flex flex-col" role="navigation">
          <ul className="space-y-1 px-2 flex-1 overflow-y-auto">
            {allNavigation.map((item) => (
              <li
                key={item.name}
                className="relative"
                onMouseEnter={() => {
                  if (!isSidebarOpen && item.subItems) {
                    showCollapsedFlyout(item.name);
                  }
                }}
                onMouseLeave={() => {
                  if (!isSidebarOpen && item.subItems) {
                    hideCollapsedFlyoutWithDelay();
                  }
                }}
              >
                {item.path ? (
                  <Link
                    to={item.path}
                    className={`flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 border-l-4 border-primary-600 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    title={isSidebarOpen ? undefined : item.name}
                  >
                    <span className="text-xl flex-shrink-0" aria-hidden="true">
                      {item.icon}
                    </span>
                    {isSidebarOpen && (
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="font-semibold truncate">{item.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.description}
                        </p>
                      </div>
                    )}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                        hasActiveSubItem(item)
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                      aria-expanded={isMenuExpanded(item.name)}
                      title={isSidebarOpen ? undefined : item.name}
                    >
                      <span
                        className="text-xl flex-shrink-0"
                        aria-hidden="true"
                      >
                        {item.icon}
                      </span>
                      {isSidebarOpen && (
                        <>
                          <div className="ml-3 flex-1 min-w-0 text-left">
                            <p className="font-semibold truncate">
                              {item.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {item.description}
                            </p>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform flex-shrink-0 ${isMenuExpanded(item.name) ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </>
                      )}
                    </button>
                    {!isSidebarOpen &&
                      item.subItems &&
                      hoveredCollapsedMenu === item.name && (
                        <div
                          className="absolute left-full top-0 ml-2 min-w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl z-40 p-2"
                          onMouseEnter={() => showCollapsedFlyout(item.name)}
                          onMouseLeave={hideCollapsedFlyoutWithDelay}
                        >
                          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {item.name}
                          </p>
                          <ul className="space-y-1">
                            {item.subItems.map((subItem) => (
                              <li key={subItem.path}>
                                <Link
                                  to={subItem.path!}
                                  className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isActive(subItem.path)
                                      ? 'bg-primary-100 text-primary-800 font-medium dark:bg-primary-900/30 dark:text-primary-300'
                                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                                  }`}
                                  aria-current={
                                    isActive(subItem.path) ? 'page' : undefined
                                  }
                                >
                                  <span className="text-lg mr-2" aria-hidden="true">
                                    {subItem.icon}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="truncate">{subItem.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {subItem.description}
                                    </p>
                                  </div>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    {isSidebarOpen &&
                      isMenuExpanded(item.name) &&
                      item.subItems && (
                        <ul className="ml-6 mt-1 space-y-1">
                          {item.subItems.map((subItem) => (
                            <li key={subItem.path}>
                              <Link
                                to={subItem.path!}
                                className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                                  isActive(subItem.path)
                                    ? 'bg-primary-100 text-primary-800 font-medium dark:bg-primary-900/30 dark:text-primary-300'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                                }`}
                                aria-current={
                                  isActive(subItem.path) ? 'page' : undefined
                                }
                              >
                                <span
                                  className="text-lg mr-2"
                                  aria-hidden="true"
                                >
                                  {subItem.icon}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="truncate">{subItem.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {subItem.description}
                                  </p>
                                </div>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 px-2 pb-1">
            {isSidebarOpen ? (
              <Link
                to="/version-history"
                title="View version history"
                className="block text-xs text-gray-500 dark:text-gray-400 text-center select-none cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                v{appVersion}
              </Link>
            ) : (
              <Link
                to="/version-history"
                className="block text-[10px] leading-tight text-gray-500 dark:text-gray-400 text-center select-none cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                aria-label={`Version v${appVersion}`}
                title="View version history"
              >
                v{appVersion}
              </Link>
            )}
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <main
        className={`pt-16 overflow-x-hidden transition-all duration-300 ease-in-out flex-1 flex flex-col min-h-0 ${
          isSidebarOpen ? 'md:ml-64' : 'md:ml-16'
        }`}
        style={mainStyle}
        role="main"
      >
        {constrainContent ? (
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 flex-1 min-h-0 overflow-y-auto">
            {children}
          </div>
        ) : (
          <div className="w-full flex-1 min-h-0 overflow-y-auto">
            {children}
          </div>
        )}
      </main>

      <FeedbackButton />
      <NotificationPanel />
      <AssistantPane />
      <SwitchOrganizationModal
        isOpen={showSwitchOrganizationModal}
        onClose={() => setShowSwitchOrganizationModal(false)}
      />
      <BookmarkViewModal
        isOpen={isBookmarkModalOpen}
        onClose={() => setIsBookmarkModalOpen(false)}
        currentPath={location.pathname}
      />
      {whatsNewData?.hasUpdates && (
        <WhatsNewModal data={whatsNewData} onDismiss={() => { void dismissWhatsNew(); }} />
      )}
    </div>
  );
}
